/**
 * Stato locale di un evento.
 *
 * Regola di fondo: **il server è l'unica fonte della verità**. Questo stato è
 * una copia di lavoro che serve a rispondere in fretta mentre l'operatore
 * digita; ogni scrittura viene confermata o ritirata dal server. L'unica cosa
 * che il client si permette di anticipare è l'aspetto del pulsante premuto, e
 * solo per il tempo di un giro di rete — con il modo di disfarla, se il server
 * risponde di no.
 *
 * Il reducer è puro e vive fuori da React: le regole su cosa succede quando due
 * operatori toccano lo stesso chip si possono verificare senza montare un
 * componente.
 */
import {
  computeRaceSummaries,
  computeTeamSummaries,
  computeTotals,
  isRemoved,
} from '@pietrochip/shared';
import type {
  ChipDto,
  EventDto,
  EventStateDto,
  ReturnLogDto,
  Totals,
} from '@pietrochip/shared';

/** Quante operazioni recenti si tengono in memoria per la colonna laterale. */
const RECENT_LOGS_LIMIT = 25;

export type PendingReturn = {
  chipId: string;
  /** Il chip com'era prima dell'anticipo: è ciò che si rimette se il server dice no. */
  previous: ChipDto | null;
  /** L'oggetto inserito in anticipo: serve a riconoscere "è ancora il mio". */
  optimistic: ChipDto;
};

export type EventState = {
  event: EventDto | null;
  /** Chip attivi per identificativo: è la forma con cui arrivano gli aggiornamenti. */
  chips: ReadonlyMap<string, ChipDto>;
  /**
   * I chip archiviati, per identificativo.
   *
   * Stanno in una mappa a parte e non nella stessa: un rimosso non deve poter
   * finire in un elenco, in un totale o in un ordinamento per sbaglio, e la
   * separazione lo rende impossibile invece che improbabile. Si popola quando
   * qualcuno apre la sezione «Rimossi» — la pagina non deve pagarli a ogni
   * caricamento — e da lì in poi lo tengono vivo i messaggi del socket.
   */
  removed: ReadonlyMap<string, ChipDto>;
  recentLogs: readonly ReturnLogDto[];
  operators: number;
  /** Restituzioni anticipate in attesa della risposta del server. */
  pending: ReadonlyMap<string, PendingReturn>;
  /** Falso finché non è arrivato il primo stato completo dal server. */
  loaded: boolean;
  /**
   * Avanza a ogni azione che può cambiare i chip in mappa.
   *
   * Serve a chi aspetta una risposta di rete iniziata in un certo momento: se
   * nel frattempo la revisione è cambiata, quella risposta è nata prima di
   * qualcosa che sappiamo già, e applicarla farebbe tornare indietro
   * l'interfaccia. Si confronta la revisione di partenza con quella attuale e
   * in caso di differenza si scarta.
   */
  revision: number;
};

export const EMPTY_EVENT_STATE: EventState = {
  event: null,
  chips: new Map(),
  removed: new Map(),
  recentLogs: [],
  operators: 0,
  pending: new Map(),
  loaded: false,
  revision: 0,
};

export type EventAction =
  | { type: 'server:state'; state: EventStateDto; operators: number }
  | { type: 'refresh:state'; state: EventStateDto }
  | { type: 'server:chip'; chip: ChipDto; log?: ReturnLogDto | null }
  | { type: 'server:chips'; chips: readonly ChipDto[] }
  | { type: 'server:chip-removed'; chip: ChipDto; log?: ReturnLogDto | null }
  | { type: 'server:chip-restored'; chip: ChipDto; log?: ReturnLogDto | null }
  | { type: 'removed:state'; chips: readonly ChipDto[] }
  | { type: 'server:event'; event: EventDto }
  | { type: 'server:presence'; operators: number }
  | { type: 'optimistic:return'; chip: ChipDto; user: { id: string; name: string }; at: string }
  | { type: 'optimistic:settle'; chipId: string }
  | { type: 'optimistic:rollback'; chipId: string }
  | { type: 'reset' };

export function eventReducer(state: EventState, action: EventAction): EventState {
  switch (action.type) {
    case 'reset':
      return EMPTY_EVENT_STATE;

    // Lo stato completo arriva all'apertura e a **ogni riconnessione**: è così
    // che un buco di rete non lascia in giro chip vecchi.
    case 'server:state':
      return {
        event: action.state.event,
        chips: new Map(action.state.chips.map((chip) => [chip.id, chip])),
        // L'archivio non arriva con `hello` — il server non manda i rimossi a chi
        // non li ha chiesti — quindi la copia che c'è si tiene. È la pagina a
        // rileggerla alla riapertura del socket, se qualcuno l'aveva aperta.
        removed: state.removed,
        recentLogs: action.state.recentLogs,
        operators: action.operators,
        pending: new Map(),
        loaded: true,
        revision: state.revision + 1,
      };

    /**
     * Stato completo letto dal ricaricamento di riserva, non arrivato dal socket.
     *
     * Assomiglia a `server:state`, ma con tre differenze: chi legge non è il
     * server che ha appena trasmesso una scrittura, è una fotografia che può
     * essere nata **prima** di una scrittura nostra ancora in volo.
     *
     * - Le attese ottimistiche **non** si azzerano: senza questo il chip
     *   tornerebbe indietro per un istante, e il pulsante lampeggerebbe.
     * - Il chip in attesa viene rimesso **sopra** la verità del server, e la
     *   sua copia precedente si aggiorna a quella verità: se poi la scrittura
     *   viene rifiutata, il ritorno indietro atterra sul dato più fresco che
     *   conosciamo invece che su uno di diversi secondi prima.
     * - Il numero di operatori non si tocca: la risposta REST non lo contiene,
     *   e sovrascriverlo farebbe sparire gli altri banchi a ogni giro.
     */
    case 'refresh:state': {
      const chips = new Map(action.state.chips.map((chip) => [chip.id, chip]));

      let pending = state.pending;
      if (state.pending.size > 0) {
        const next = new Map(state.pending);

        for (const [chipId, entry] of state.pending) {
          const truth = chips.get(chipId);
          if (!truth) {
            // Il chip non c'è più: è stato eliminato da un altro banco, e
            // rimetterlo lo farebbe ricomparire dal nulla.
            next.delete(chipId);
            continue;
          }

          // Stessa **istanza** di prima, non una copia: `optimistic:rollback`
          // riconosce il proprio chip confrontando i riferimenti.
          chips.set(chipId, entry.optimistic);
          next.set(chipId, { ...entry, previous: truth });
        }

        pending = next;
      }

      return {
        ...state,
        event: action.state.event,
        chips,
        recentLogs: mergeLogs(state.recentLogs, action.state.recentLogs),
        pending,
        loaded: true,
        revision: state.revision + 1,
      };
    }

    case 'server:chip':
      return {
        ...state,
        chips: withChip(state.chips, action.chip),
        recentLogs: action.log ? pushLog(state.recentLogs, action.log) : state.recentLogs,
        // La conferma del server chiude l'attesa: da qui in poi lo stato locale
        // è quello vero, e non c'è più niente da ritirare.
        pending: withoutPending(state.pending, action.chip.id),
        revision: state.revision + 1,
      };

    case 'server:chips':
      return {
        ...state,
        chips: action.chips.reduce(
          (chips, chip) => withChip(chips, chip),
          state.chips,
        ),
        revision: state.revision + 1,
      };

    /**
     * Un chip è stato archiviato: esce dagli attivi ed entra fra i rimossi.
     *
     * Esce **dalla mappa**, non solo dagli elenchi: così totali, gare e «ultimi
     * rientrati» — che leggono tutti da lì — seguono da soli, senza che nessuno
     * debba ricordarsi di filtrare. E l'attesa ottimistica su quel chip si
     * chiude: non c'è più niente da confermare, e un rollback lo farebbe
     * ricomparire dal nulla.
     */
    case 'server:chip-removed':
      return {
        ...state,
        chips: withoutChip(state.chips, action.chip.id),
        removed: withChip(state.removed, action.chip),
        recentLogs: action.log ? pushLog(state.recentLogs, action.log) : state.recentLogs,
        pending: withoutPending(state.pending, action.chip.id),
        revision: state.revision + 1,
      };

    case 'server:chip-restored':
      return {
        ...state,
        chips: withChip(state.chips, action.chip),
        removed: withoutChip(state.removed, action.chip.id),
        recentLogs: action.log ? pushLog(state.recentLogs, action.log) : state.recentLogs,
        revision: state.revision + 1,
      };

    /** L'elenco completo dei rimossi, letto quando si apre la sezione. */
    case 'removed:state':
      return {
        ...state,
        removed: new Map(action.chips.map((chip) => [chip.id, chip])),
      };

    case 'server:event':
      // Anche qui la revisione avanza: una lettura partita prima di una
      // rinomina riporterebbe il nome vecchio.
      return { ...state, event: action.event, revision: state.revision + 1 };

    case 'server:presence':
      return { ...state, operators: action.operators };

    case 'optimistic:return': {
      const current = state.chips.get(action.chip.id) ?? null;
      const optimistic: ChipDto = {
        ...action.chip,
        returnedAt: action.at,
        returnedBy: action.user.id,
        returnedByName: action.user.name,
      };

      const pending = new Map(state.pending);
      pending.set(action.chip.id, {
        chipId: action.chip.id,
        previous: current,
        optimistic,
      });

      return {
        ...state,
        chips: withChip(state.chips, optimistic),
        pending,
        revision: state.revision + 1,
      };
    }

    case 'optimistic:settle':
      return { ...state, pending: withoutPending(state.pending, action.chipId) };

    /**
     * Il server ha rifiutato la scrittura: si torna indietro.
     *
     * Solo se il chip è **ancora quello che avevamo messo noi**: se nel
     * frattempo è arrivato un aggiornamento vero (magari un altro operatore ha
     * chiuso lo stesso chip), quello vince e non si tocca.
     */
    case 'optimistic:rollback': {
      const entry = state.pending.get(action.chipId);
      if (!entry) return state;

      const current = state.chips.get(action.chipId);
      const stillOurs = current !== undefined && current === entry.optimistic;

      return {
        ...state,
        chips: stillOurs
          ? entry.previous
            ? withChip(state.chips, entry.previous)
            : withoutChip(state.chips, action.chipId)
          : state.chips,
        pending: withoutPending(state.pending, action.chipId),
        // Avanza solo se i chip sono davvero cambiati: se vince
        // l'aggiornamento di un altro operatore qui non si tocca niente.
        revision: stillOurs ? state.revision + 1 : state.revision,
      };
    }

    default:
      return state;
  }
}

// ── Operazioni sulle mappe, sempre restituendo una mappa nuova ───────────────

function withChip(
  chips: ReadonlyMap<string, ChipDto>,
  chip: ChipDto,
): ReadonlyMap<string, ChipDto> {
  const next = new Map(chips);
  next.set(chip.id, chip);
  return next;
}

function withoutChip(
  chips: ReadonlyMap<string, ChipDto>,
  chipId: string,
): ReadonlyMap<string, ChipDto> {
  if (!chips.has(chipId)) return chips;
  const next = new Map(chips);
  next.delete(chipId);
  return next;
}

function withoutPending(
  pending: ReadonlyMap<string, PendingReturn>,
  chipId: string,
): ReadonlyMap<string, PendingReturn> {
  if (!pending.has(chipId)) return pending;
  const next = new Map(pending);
  next.delete(chipId);
  return next;
}

/** Inserisce l'operazione in testa, evitando i doppioni che arrivano dal socket. */
function pushLog(
  logs: readonly ReturnLogDto[],
  log: ReturnLogDto,
): readonly ReturnLogDto[] {
  if (logs.some((entry) => entry.id === log.id)) return logs;
  return [log, ...logs].slice(0, RECENT_LOGS_LIMIT);
}

/**
 * Unisce il registro che abbiamo con quello appena letto dal server.
 *
 * Non basta tenere il più recente: una risposta di rete può essere nata prima
 * di un'operazione che il socket ci ha già annunciato, e in quel caso il
 * registro letto è più vecchio di quello che abbiamo in mano. Si uniscono per
 * identificativo e si riordina per ora, così nessuna operazione si perde e
 * nessuna compare due volte.
 */
function mergeLogs(
  current: readonly ReturnLogDto[],
  incoming: readonly ReturnLogDto[],
): readonly ReturnLogDto[] {
  const byId = new Map<string, ReturnLogDto>();
  for (const entry of [...incoming, ...current]) {
    if (!byId.has(entry.id)) byId.set(entry.id, entry);
  }

  return [...byId.values()]
    .sort((a, b) => toTime(b.at) - toTime(a.at))
    .slice(0, RECENT_LOGS_LIMIT);
}

// ── Selettori ────────────────────────────────────────────────────────────────

/**
 * I chip attivi in ordine di codice: è l'ordine in cui l'operatore li cerca a
 * vista.
 *
 * Il filtro sui rimossi è una seconda rete: la mappa non dovrebbe contenerne,
 * perché il server non li manda e il reducer li toglie. Ma se uno ci arrivasse
 * — una risposta vecchia rimasta in volo, un selettore scritto male — non deve
 * poter comparire in un elenco che l'operatore legge per decidere cosa manca.
 */
export function selectChips(state: EventState): ChipDto[] {
  return [...state.chips.values()]
    .filter((chip) => !isRemoved(chip))
    .sort((a, b) => (a.code < b.code ? -1 : a.code > b.code ? 1 : 0));
}

/** I chip archiviati, dal rimosso più recente. */
export function selectRemovedChips(state: EventState): ChipDto[] {
  return [...state.removed.values()].sort((a, b) => toTime(b.removedAt) - toTime(a.removedAt));
}

export function selectTotals(state: EventState): Totals {
  return computeTotals([...state.chips.values()]);
}

export function selectRaces(state: EventState) {
  return computeRaceSummaries([...state.chips.values()]);
}

/**
 * L'avanzamento per squadra.
 *
 * Si legge dai chip e non da una chiamata a parte: la dashboard dei mancanti
 * deve restare giusta mentre si lavora, e i chip arrivano già aggiornati dal
 * socket. Chiedere al server un conteggio per squadra significherebbe averne
 * due — quello vero e quello appena calcolato — e dover decidere quale mostrare.
 */
export function selectTeamSummaries(state: EventState) {
  return computeTeamSummaries([...state.chips.values()]);
}

/**
 * I chip restituiti di recente, dal più recente.
 *
 * Si legge dai chip e non dal registro: dopo una riconnessione il registro
 * contiene solo le ultime operazioni, mentre lo stato dei chip è completo.
 */
export function selectRecentlyReturned(state: EventState, limit = 8): ChipDto[] {
  return [...state.chips.values()]
    .filter((chip) => !isRemoved(chip) && chip.returnedAt !== null)
    .sort((a, b) => toTime(b.returnedAt) - toTime(a.returnedAt))
    .slice(0, limit);
}

function toTime(value: string | null): number {
  if (!value) return 0;
  const time = new Date(value).getTime();
  return Number.isNaN(time) ? 0 : time;
}
